'use strict';
'use es6';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createLockedTracker = undefined;

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _messages = require('./common/messages');

var _helpers = require('./common/helpers');

var _schemas = require('./schemas');

var SPECIAL_PROPERTIES = ['eventKey', 'namespace', 'lang', 'email', 'userId', 'hubId', 'hstc'];

var getDefinition = function getDefinition(_ref, eventKey, eventProperties) {
  var events = _ref.events,
      logError = _ref.logError,
      onError = _ref.onError,
      lookupDefinition = _ref.lookupDefinition;

  try {
    return lookupDefinition(events, eventKey, eventProperties);
  } catch (err) {
    logError(err);
    onError(err);
    return null;
  }
};

var getIdentifiers = function getIdentifiers(_ref2, _ref3) {
  var createIdentifiers = _ref2.createIdentifiers,
      isExternalHost = _ref2.isExternalHost,
      logError = _ref2.logError,
      onError = _ref2.onError;
  var email = _ref3.email,
      userId = _ref3.userId,
      hubId = _ref3.hubId,
      hstc = _ref3.hstc;

  try {
    return createIdentifiers({
      email: email,
      userId: userId,
      hubId: hubId,
      hstc: hstc
    }, isExternalHost);
  } catch (err) {
    logError(err);
    onError(err);
    return null;
  }
};

var createEvent = function createEvent(_ref4, definition, identifiers, eventProperties) {
  var createEventPayload = _ref4.createEventPayload,
      logError = _ref4.logError,
      onError = _ref4.onError;

  try {
    return createEventPayload(definition, eventProperties, identifiers);
  } catch (err) {
    logError(err);
    onError(err);
    return null;
  }
};

var debugEvent = function debugEvent(_ref5, eventKey, eventProperties, event) {
  var logMessage = _ref5.logMessage;

  logMessage((0, _messages.debugLog)('event tracked', {
    eventKey: '"' + eventKey + '"',
    eventName: event.what_event,
    eventNamespace: event.where_app,
    eventProperties: (0, _helpers.omit)(eventProperties, SPECIAL_PROPERTIES)
  }));
  var prettifiedPayload = JSON.parse(JSON.stringify(event));
  prettifiedPayload.what_extra_json = JSON.parse(prettifiedPayload.what_extra_json);
  logMessage(prettifiedPayload);
};

var trackEvent = function trackEvent(config, eventKey, eventProperties) {
  var definition = getDefinition(config, eventKey, eventProperties);
  var identifiers = getIdentifiers(config, eventProperties);
  if (!definition || !identifiers) return false;

  var event = createEvent(config, definition, identifiers, eventProperties);
  if (!event) return false;

  var debug = typeof config.debug === 'function' ? config.debug() : config.debug;
  if (debug) {
    try {
      debugEvent(config, eventKey, eventProperties, event);
    } catch (err) {
      /* NoOp */
    }
  }

  config.sendToClient(config, identifiers, event);
  return true;
};

var mergeProperties = function mergeProperties(eventKey, eventProperties, trackerProperties) {
  return (0, _helpers.defaults)(_extends({ eventKey: eventKey }, eventProperties), (0, _helpers.shallowCopy)(trackerProperties));
};

var parseConfig = function parseConfig(trackerDependencies, config) {
  if (!config || (typeof config === 'undefined' ? 'undefined' : _typeof(config)) !== 'object') {
    throw (0, _messages.configError)('Invalid argument. The "createTracker" function requires to be passed a config argument of type "object". Received type "' + (typeof config === 'undefined' ? 'undefined' : _typeof(config)) + '".');
  }

  /*
   *  Apply defualts to schemas.
   *  Defaults such as onError are not known until runtime.
   */
  var trackerConfigSchemaWithDefaults = _schemas.trackerConfigSchema.mutate(function (schema) {
    schema.debug.default = trackerDependencies.getDebug;
    schema.onError.default = trackerDependencies.reportError;
    return schema;
  });
  var trackerPropertiesSchemaWithDefaults = _schemas.trackerPropertiesSchema.mutate(function (schema) {
    schema.email.default = trackerDependencies.getEmail;
    schema.hubId.default = trackerDependencies.getHubId;
    schema.hstc.default = trackerDependencies.getHstc;
    schema.lang.default = trackerDependencies.getLang;
    return schema;
  });

  /*
   *  Normalize & validate tracker config + properties.
   */
  var parsedConfig = trackerConfigSchemaWithDefaults.normalize(config);
  trackerConfigSchemaWithDefaults.validate(parsedConfig, '"createTracker"');
  var parsedTrackerProperties = trackerPropertiesSchemaWithDefaults.normalize(parsedConfig.properties);
  trackerPropertiesSchemaWithDefaults.validate(parsedTrackerProperties, '"createTracker"');
  var parsedDefinitions = trackerDependencies.parseDefinitions(parsedConfig.events, '"createTracker"');

  return _extends({
    events: parsedDefinitions,
    properties: parsedTrackerProperties
  }, (0, _helpers.omit)(parsedConfig, ['events', 'properties']), trackerDependencies);
};

var createLockedTracker = exports.createLockedTracker = function createLockedTracker(trackerDependencies) {
  var config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var parsedConfig = parseConfig(trackerDependencies, config);
  var lastKnownPropertyCache = {};
  var properties = _extends({}, parsedConfig.properties);
  var propertiesAreDirty = true;

  /*
   *  Syncs lastKnownEventProperties each
   *  time an event is tracked with eventProperties.
   */
  var lastKnownPropertyCacheSync = function lastKnownPropertyCacheSync(eventProperties) {
    if (parsedConfig.lastKnownEventProperties && parsedConfig.lastKnownEventProperties.length) {
      parsedConfig.lastKnownEventProperties.forEach(function (key) {
        var value = eventProperties[key];
        if (value !== undefined) {
          lastKnownPropertyCache[key] = value;
        } else {
          value = lastKnownPropertyCache[key];
        }
        if (value && value !== properties[key]) {
          properties[key] = value;
        }
      });
    }
  };

  return {
    clone: function clone() {
      var overrides = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      if (!overrides || (typeof overrides === 'undefined' ? 'undefined' : _typeof(overrides)) !== 'object') {
        throw (0, _messages.genericError)('Invalid argument. The "clone" method requires to be passed a valid tracker config of type "object". Received type "' + (typeof overrides === 'undefined' ? 'undefined' : _typeof(overrides)) + '".');
      }

      var mergedConfig = (0, _helpers.omit)((0, _helpers.defaults)(overrides, parsedConfig), Object.keys(trackerDependencies));
      var supportedProperties = (0, _helpers.omit)(properties, _schemas.trackerPropertiesSchema.getKeys(), false);
      var arbitraryProperties = (0, _helpers.omit)(properties, Object.keys(supportedProperties));

      mergedConfig.properties = (0, _helpers.defaults)(overrides.properties || {}, supportedProperties);

      if (parsedConfig.preserveTrackerProperties) {
        mergedConfig.properties = (0, _helpers.defaults)(mergedConfig.properties, arbitraryProperties);
      }

      return createLockedTracker(trackerDependencies, mergedConfig);
    },

    config: _extends({}, (0, _helpers.omit)(parsedConfig, ['properties']), {
      properties: properties
    }),

    setProperties: function setProperties() {
      var newProperties = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      if (!newProperties || (typeof newProperties === 'undefined' ? 'undefined' : _typeof(newProperties)) !== 'object') {
        throw (0, _messages.genericError)('Invalid argument. The "setProperties" method requires to be passed a properties argument of type "object". Received type "' + (typeof newProperties === 'undefined' ? 'undefined' : _typeof(newProperties)) + '".');
      }

      Object.keys(newProperties).forEach(function (key) {
        var value = newProperties[key];
        properties[key] = value;
      });

      propertiesAreDirty = true;
    },

    track: function track(eventKey) {
      var eventProperties = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (!eventKey || typeof eventKey !== 'string') {
        throw (0, _messages.genericError)('Invalid argument. The "track" method requires to be passed an eventKey of type "string". Received type "' + (typeof eventKey === 'undefined' ? 'undefined' : _typeof(eventKey)) + '".');
      }

      if (!eventProperties || (typeof eventProperties === 'undefined' ? 'undefined' : _typeof(eventProperties)) !== 'object') {
        throw (0, _messages.genericError)('Invalid argument. The "track" method requires the 2nd arg to be passed eventProperties of type "object". Received type "' + (typeof eventProperties === 'undefined' ? 'undefined' : _typeof(eventProperties)) + '".');
      }

      var mergedProperties = mergeProperties(eventKey, eventProperties, properties);
      lastKnownPropertyCacheSync(mergedProperties);

      if (propertiesAreDirty) {
        (0, _helpers.resolveAsyncProperties)(properties, parsedConfig.onError, function (resolved) {
          properties = (0, _helpers.defaults)(resolved, properties);
          propertiesAreDirty = false;
          trackEvent(parsedConfig, eventKey, mergeProperties(eventKey, eventProperties, properties));
        });
      } else {
        trackEvent(parsedConfig, eventKey, mergedProperties);
      }
    }
  };
};